import { useTranslation } from 'react-i18next';
import { Search, TrendingUp, Clock } from 'lucide-react';
import { HelpSearch } from '@/components/help/HelpSearch';
import { HelpCategoryCard } from '@/components/help/HelpCategoryCard';
import { HelpArticleCard } from '@/components/help/HelpArticleCard';
import { PageLayout } from '@/components/ui/PageLayout';
import { TYPE } from '@/lib/typography';
import { useHelpCategories, useHelpArticles, useHelpPopular, useHelpRecent } from '@/hooks/useHelp';

export function HelpCenter() {
  const { t } = useTranslation();
  const categoriesQuery = useHelpCategories();
  const articlesQuery = useHelpArticles();
  const popularQuery = useHelpPopular(6);
  const recentQuery = useHelpRecent(3);

  const categories = categoriesQuery.data ?? [];
  const articles = articlesQuery.data ?? [];
  const popular = popularQuery.data ?? [];
  const recent = recentQuery.data ?? [];

  // Count articles per category
  const articleCounts = articles.reduce(
    (acc, article) => {
      acc[article.category] = (acc[article.category] ?? 0) + 1;
      return acc;
    },
    {} as Record<string, number>,
  );

  return (
    <PageLayout
      title={t('help.hero_title', 'Hvordan kan vi hjælpe dig?')}
      subtitle={t('help.hero_subtitle', 'Søg efter svar, guides og tutorials')}
    >
      <div className="max-w-2xl">
        <HelpSearch
          placeholder={t('help.search_placeholder', 'Søg i hjælpecenteret...')}
          autoFocus
        />
      </div>

      <div className="mt-s5 flex flex-col gap-s7">
        {recent.length > 0 && (
          <section>
            <div className="mb-s4 flex items-center gap-s2">
              <Clock className="h-5 w-5 text-ds-muted" />
              <h2 className={TYPE.heading}>
                {t('help.recently_viewed', 'Senest set')}
              </h2>
            </div>
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {recent.map((article) => (
                <HelpArticleCard key={article.id} article={article} showCategory />
              ))}
            </div>
          </section>
        )}

        <section>
          <div className="mb-s4 flex items-center gap-s2">
            <Search className="h-5 w-5 text-ds-muted" />
            <h2 className={TYPE.heading}>
              {t('help.browse_categories', 'Gennemse kategorier')}
            </h2>
          </div>

          {categoriesQuery.isLoading ? (
            <div className="py-s7 text-center text-ds-muted">{t('app.loading', 'Indlæser...')}</div>
          ) : categories.length === 0 ? (
            <div className="rounded-card border border-ds-line bg-ds-card p-s7 text-center">
              <p className="text-ds-muted">
                {t('help.no_categories', 'Ingen kategorier tilgængelige')}
              </p>
            </div>
          ) : (
            <div className="grid gap-s4 sm:grid-cols-2 lg:grid-cols-3">
              {categories.map((category) => (
                <HelpCategoryCard
                  key={category.id}
                  category={category}
                  articleCount={articleCounts[category.id]}
                />
              ))}
            </div>
          )}
        </section>

        {popular.length > 0 && (
          <section>
            <div className="mb-s4 flex items-center gap-s2">
              <TrendingUp className="h-5 w-5 text-ds-muted" />
              <h2 className={TYPE.heading}>
                {t('help.popular_articles', 'Populære artikler')}
              </h2>
            </div>
            <div className="grid gap-s4 sm:grid-cols-2 lg:grid-cols-3">
              {popular.map((article) => (
                <HelpArticleCard key={article.id} article={article} showCategory />
              ))}
            </div>
          </section>
        )}
      </div>
    </PageLayout>
  );
}
